import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PayRun, 
  PayRunFilterParams, 
  PaginationParams,
  PayRunItem,
  Payslip
} from '@/types';
import { 
  useFetch, 
  usePaginatedFetch, 
} from './useApi';
import { apiClient } from '@/lib/api';

// Query keys
const PAY_RUNS_KEY = 'pay-runs';
const PAYSLIPS_KEY = 'payslips';

// ==================== Pay Runs ====================

export function usePayRuns(params?: PayRunFilterParams & PaginationParams) {
  return usePaginatedFetch<{ data: PayRun[]; meta: { total: number } }>(
    PAY_RUNS_KEY,
    '/payroll/pay-runs',
    {
      page: params?.page || 1,
      limit: params?.limit || 20,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    },
    {
      status: params?.status,
      dateFrom: params?.dateFrom,
      dateTo: params?.dateTo,
    }
  );
}

export function usePayRun(id: string) {
  return useFetch<PayRun>(
    [PAY_RUNS_KEY, id],
    `/payroll/pay-runs/${id}`,
    undefined,
    { enabled: !!id }
  );
}

export function useCreatePayRun() {
  const queryClient = useQueryClient();
  
  return useMutation<PayRun, Error, { 
    periodStartDate: string; 
    periodEndDate: string; 
    paymentDate: string;
    employeeIds?: string[];
  }>({
    mutationFn: (data) => apiClient.post('/payroll/pay-runs', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY] });
    },
  });
}

export function useUpdatePayRun() {
  const queryClient = useQueryClient();
  
  return useMutation<PayRun, Error, { id: string; data: Partial<PayRun> }>({
    mutationFn: ({ id, data }) => apiClient.put(`/payroll/pay-runs/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY, variables.id] });
    },
  });
}

export function useDeletePayRun() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/payroll/pay-runs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY] });
    },
  });
}

export function useCalculatePayRun() {
  const queryClient = useQueryClient();
  
  return useMutation<PayRun, Error, string>({
    mutationFn: (id) => apiClient.post(`/payroll/pay-runs/${id}/calculate`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY, id] });
    },
  });
}

export function useApprovePayRun() {
  const queryClient = useQueryClient();
  
  return useMutation<PayRun, Error, string>({
    mutationFn: (id) => apiClient.patch(`/payroll/pay-runs/${id}/approve`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY, id] });
    },
  });
}

export function useMarkPaid() {
  const queryClient = useQueryClient();
  
  return useMutation<PayRun, Error, string>({
    mutationFn: (id) => apiClient.patch(`/payroll/pay-runs/${id}/mark-paid`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY, id] });
    },
  });
}

export function useAddPayRunItem() {
  const queryClient = useQueryClient();
  
  return useMutation<PayRunItem, Error, { payRunId: string; data: Partial<PayRunItem> }>({
    mutationFn: ({ payRunId, data }) => 
      apiClient.post(`/payroll/pay-runs/${payRunId}/items`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY, variables.payRunId] });
    },
  });
}

export function useUpdatePayRunItem() {
  const queryClient = useQueryClient();
  
  return useMutation<PayRunItem, Error, { 
    payRunId: string; 
    itemId: string; 
    data: Partial<PayRunItem> 
  }>({
    mutationFn: ({ payRunId, itemId, data }) => 
      apiClient.put(`/payroll/pay-runs/${payRunId}/items/${itemId}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY, variables.payRunId] });
    },
  });
}

export function useDeletePayRunItem() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, { payRunId: string; itemId: string }>({
    mutationFn: ({ payRunId, itemId }) => 
      apiClient.delete(`/payroll/pay-runs/${payRunId}/items/${itemId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAY_RUNS_KEY, variables.payRunId] });
    },
  });
}

// ==================== Payslips ====================

export function usePayslips(payRunId: string) {
  return useFetch<Payslip[]>(
    [PAYSLIPS_KEY, payRunId],
    payRunId ? `/payroll/pay-runs/${payRunId}/payslips` : '/payroll/payslips',
    undefined,
    { enabled: true }
  );
}

export function usePayslip(id: string) {
  return useFetch<Payslip>(
    [PAYSLIPS_KEY, id],
    `/payroll/payslips/${id}`,
    undefined,
    { enabled: !!id }
  );
}

export function useDownloadPayslip() {
  return useMutation<Blob, Error, string>({
    mutationFn: (id) => apiClient.get(`/payroll/payslips/${id}/download`, { responseType: 'blob' }),
  });
}

export function useEmailPayslip() {
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.post(`/payroll/payslips/${id}/email`, {}),
  });
}

export function useEmailAllPayslips() {
  return useMutation<{ sent: number; failed: number }, Error, string>({
    mutationFn: (payRunId) => apiClient.post(`/payroll/pay-runs/${payRunId}/email-payslips`, {}),
  });
}
